/* eslint-disable @typescript-eslint/no-explicit-any */
import { useApolloClient } from '@apollo/client'
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import React from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useParams } from 'react-router'
import {
  CREATE_PROCEDURE,
  FETCH_PROCEDURE,
  UPDATE_PROCEDURE
} from '../../../graphql/queries'
import { useListStyles } from '../../Budget/styles'
import { CancelButton, SuccessButton } from '../../Buttons'
import { ProcedureItem } from './types'

type IFormInput = {
  name: string
  internalAnnotations: string
  enable: boolean
  items: ProcedureItem[]
}

const AmountCustomInput = (props: any) => (
  <TextField placeholder="Preço" {...props} />
)

const ProceduresSlug: React.FC = () => {
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors }
  } = useForm<IFormInput>({})
  const { fields, append, remove } = useFieldArray<ProcedureItem>({
    control,
    name: 'items'
  })
  const classes = useListStyles()
  const router = useHistory()
  const client = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = React.useState(false)
  const params = useParams<{ id?: string }>()
  const isUpdate = params?.id && params?.id !== 'novo'

  React.useEffect(() => {
    if (!isUpdate) {
      reset({
        items: [
          {
            name: 'Novo item',
            code: '0',
            price: {
              float: 0
            }
          }
        ]
      })

      return
    }
    client
      .query({
        query: FETCH_PROCEDURE,
        variables: {
          id: params.id
        }
      })
      .then(res => {
        const procedure = res?.data?.procedures?.edges?.[0]
        const items = procedure?.items?.map((item: any) => ({
          code: item?.code || '',
          name: item?.name || '',
          price: {
            float: item.price?.float || 0
          }
        }))
        reset({
          name: procedure?.name,
          internalAnnotations: procedure?.internalAnnotations,
          enable: procedure?.enable,
          items
        })
        trigger()
      })
  }, [params.id])

  const onSubmit = (data: IFormInput) => {
    setLoading(true)

    client
      .mutate({
        mutation: isUpdate ? UPDATE_PROCEDURE : CREATE_PROCEDURE,
        variables: {
          ...(isUpdate ? { filter: { ids: [params.id] } } : {}),
          input: {
            name: data.name,
            enable: data.enable || false,
            totalPrice:
              data.items?.reduce(
                (acc, item) => (acc += +item.price.float || 0),
                0
              ) || 0,
            internalAnnotations: data.internalAnnotations,
            items: data.items?.map(item => ({
              name: item.name,
              code: item.code,
              price: item.price.float
            }))
          }
        }
      })
      .then(({ data }) => {
        enqueueSnackbar(
          data[isUpdate ? 'updateProcedures' : 'createProcedures'].message,
          {
            variant: 'success',
            autoHideDuration: 2000
          }
        )
        router.push('/configuracao/procedimentos')
      })
      .finally(() => setLoading(false))
  }

  return (
    <div className={classes.root}>
      <div className={classes.list}>
        <h1 className={classes.title}>Novo Procedimento Cirúrgico</h1>
        <h2 className={classes.description}>
          Crie um novo procedimento cirúrgico e adicione um conjunto de itens
        </h2>
        <br />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item xs={6}>
              <Controller
                name="name"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Este campo é obrigatório'
                }}
                render={({ onChange, value }) => (
                  <TextField
                    label="Nome"
                    autoFocus
                    fullWidth
                    value={value}
                    onChange={onChange}
                    margin="dense"
                    error={!!errors.name?.message}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Controller
                    name="enable"
                    control={control}
                    render={props => (
                      <Switch
                        {...props}
                        checked={props.value || false}
                        onChange={e => props.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={'Ativo'}
              />
            </Grid>
          </Grid>
          <br />
          <Grid container justify="space-between" alignItems="center">
            <Grid item xs={12}>
              <Controller
                name="internalAnnotations"
                control={control}
                defaultValue=""
                render={({ onChange, value }) => (
                  <TextField
                    label="Observações"
                    autoFocus
                    rows={5}
                    multiline
                    fullWidth
                    value={value}
                    onChange={onChange}
                    margin="dense"
                    helperText="Este campo é apenas para observações internas"
                  />
                )}
              />
            </Grid>
          </Grid>
          <br />
          <Grid container justify="space-between">
            <Grid item xs={6}>
              <Typography variant="h5">Procedimentos</Typography>
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                color="primary"
                type="button"
                fullWidth
                onClick={() =>
                  append({
                    name: 'Novo item',
                    price: {
                      float: 0
                    },
                    code: '0'
                  })
                }
              >
                Adicionar item
              </Button>
            </Grid>
          </Grid>
          <br />
          <Grid container justify="space-between" alignItems="center">
            {fields.map((item, index) => {
              return (
                <React.Fragment key={index}>
                  <Grid item xs={3}>
                    <Controller
                      name={`items[${index}].name`}
                      control={control}
                      rules={{
                        required: 'Este campo é obrigatório'
                      }}
                      defaultValue={item.name}
                      as={
                        <TextField
                          label="Nome"
                          fullWidth
                          margin="dense"
                          error={!!errors.items?.[index]?.name?.message}
                          helperText={
                            errors.items &&
                            errors.items[index] &&
                            errors.items[index]?.name?.message
                          }
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Controller
                      name={`items[${index}].code`}
                      control={control}
                      rules={{
                        required: 'Este campo é obrigatório'
                      }}
                      defaultValue={item.code}
                      as={
                        <TextField
                          label="Código"
                          fullWidth
                          margin="dense"
                          error={!!errors.items?.[index]?.code?.message}
                          helperText={
                            errors.items &&
                            errors.items[index] &&
                            errors.items[index]?.code?.message
                          }
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Box mt={2}>
                      <Controller
                        name={`items[${index}].price.float`}
                        control={control}
                        rules={{
                          required: 'Este campo é obrigatório'
                        }}
                        defaultValue={item.price?.float || 0}
                        render={({ onChange, value }) => (
                          <NumberFormat
                            style={{ width: '100%' }}
                            prefix="R$ "
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            value={value.float}
                            onValueChange={values =>
                              onChange(values.floatValue)
                            }
                            allowNegative={false}
                            allowedDecimalSeparators={['.', ',']}
                            customInput={AmountCustomInput}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="button"
                      onClick={() => remove(index)}
                    >
                      Remover
                    </Button>
                  </Grid>
                  <br />
                </React.Fragment>
              )
            })}
          </Grid>
          <br />
          <br />
          <Grid container spacing={3} justify="space-between">
            <Grid item xs={5}>
              <CancelButton
                fullWidth
                type="button"
                onClick={() => router.goBack()}
              >
                Cancelar
              </CancelButton>
            </Grid>
            <Grid item xs={5}>
              <SuccessButton
                disabled={loading || !fields.length}
                fullWidth
                type="submit"
              >
                {loading ? 'Aguarde...' : isUpdate ? 'Atualizar' : 'Criar'}
              </SuccessButton>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  )
}

export default ProceduresSlug
